import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  KeyRound, Webhook, FileDown, Upload, RefreshCcw, Eye, EyeOff,
  CheckCircle2, AlertTriangle, PlayCircle, Loader2, Copy,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

/**
 * DeliveryConfigSection
 * ---------------------
 * Drops into the product-files modal. Lets a creator pick how their
 * product is delivered to buyers at purchase time:
 *   1. file (default) — buyer gets download links, current behaviour
 *   2. license_key_vault — creator pre-uploads keys, system auto-assigns
 *   3. webhook — system POSTs to creator's API which provisions a key
 *
 * Designed to be unobtrusive: collapses into a small toggle row by default
 * so non-API creators barely see it.
 */
export default function DeliveryConfigSection({ product, onUpdated }) {
  const [method, setMethod] = useState(product?.delivery_method || 'file');
  const [webhookUrl, setWebhookUrl] = useState(product?.webhook_url || '');
  const [instructions, setInstructions] = useState(
    product?.delivery_instructions || ''
  );
  const [secretRevealed, setSecretRevealed] = useState(false);
  const [savedSecret, setSavedSecret] = useState(product?.webhook_secret || '');
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState(null);
  const [uploadingKeys, setUploadingKeys] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const keysFileRef = useRef(null);

  // Track latest method via ref so loadStats doesn't re-fire on every render
  const methodRef = useRef(method);
  useEffect(() => { methodRef.current = method; }, [method]);

  const loadStats = useCallback(async () => {
    if (!product?.product_id) return;
    if (methodRef.current !== 'license_key_vault') return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `/products/${product.product_id}/license-keys/stats`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStats(res.data);
    } catch {
      setStats(null);
    }
  }, [product?.product_id]);

  useEffect(() => { loadStats(); }, [loadStats, method]);

  const save = async () => {
    if (method === 'webhook') {
      if (!webhookUrl.trim().startsWith('https://')) {
        toast.error('Webhook URL must start with https://');
        return;
      }
    }
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const res = await axios.put(
        `/products/${product.product_id}/delivery`,
        {
          delivery_method: method,
          webhook_url: method === 'webhook' ? webhookUrl.trim() : null,
          instructions: instructions.trim() || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.webhook_secret) {
        setSavedSecret(res.data.webhook_secret);
        setSecretRevealed(true);
      }
      toast.success('Delivery method saved');
      if (onUpdated) onUpdated(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleKeysUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      setUploadingKeys(true);
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', f);
      const res = await axios.post(
        `/products/${product.product_id}/license-keys/upload`,
        fd,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      const { uploaded_count, duplicates_skipped } = res.data || {};
      toast.success(
        `Uploaded ${uploaded_count} key${uploaded_count === 1 ? '' : 's'}` +
        (duplicates_skipped > 0 ? ` (${duplicates_skipped} duplicates skipped)` : '')
      );
      e.target.value = '';
      await loadStats();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingKeys(false);
    }
  };

  const fireTest = async () => {
    try {
      setTesting(true);
      setTestResult(null);
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `/products/${product.product_id}/webhook/test`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTestResult(res.data);
      if (res.data?.ok) toast.success('Test fire succeeded');
      else toast.error(`Test fire failed (${res.data?.error || res.data?.status_code || 'unknown'})`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Test failed');
    } finally {
      setTesting(false);
    }
  };

  const copySecret = async () => {
    try {
      await navigator.clipboard.writeText(savedSecret);
      toast.success('Copied');
    } catch {
      toast.error('Copy failed');
    }
  };

  return (
    <div
      data-testid="delivery-config-section"
      className="rounded-xl border border-white/10 bg-neutral-950 p-4 space-y-4"
    >
      <div>
        <h4 className="text-base font-semibold text-white">Delivery method</h4>
        <p className="mt-1 text-xs text-white/50">
          How buyers receive their product after purchase. Most products use
          file downloads. API/SaaS creators use the license-key rails.
        </p>
      </div>

      <div className="grid gap-2">
        <MethodOption
          testId="delivery-file"
          icon={<FileDown className="h-4 w-4" />}
          title="File download"
          subtitle="Buyer gets secure download links to your uploaded files"
          selected={method === 'file'}
          onSelect={() => setMethod('file')}
        />
        <MethodOption
          testId="delivery-vault"
          icon={<KeyRound className="h-4 w-4" />}
          title="License Key Vault"
          subtitle="Pre-upload a list of keys; we hand one to each buyer"
          selected={method === 'license_key_vault'}
          onSelect={() => setMethod('license_key_vault')}
        />
        <MethodOption
          testId="delivery-webhook"
          icon={<Webhook className="h-4 w-4" />}
          title="Webhook"
          subtitle="We POST to your API on each sale; you provision the key"
          selected={method === 'webhook'}
          onSelect={() => setMethod('webhook')}
        />
      </div>

      {/* Per-method config */}
      {method === 'license_key_vault' && (
        <div className="rounded-lg border border-white/10 bg-neutral-900 p-4 space-y-3">
          {stats && (
            <div className="flex flex-wrap gap-3 text-xs">
              <Stat label="Unused" value={stats.unused} accent="text-[#00FF94]" />
              <Stat label="Assigned" value={stats.assigned} accent="text-[#00F0FF]" />
              <Stat label="Total" value={stats.total} accent="text-white" />
            </div>
          )}
          <input
            ref={keysFileRef}
            type="file"
            accept=".csv,.txt"
            className="hidden"
            onChange={handleKeysUpload}
            data-testid="vault-upload-input"
          />
          <Button
            data-testid="vault-upload-button"
            onClick={() => keysFileRef.current?.click()}
            disabled={uploadingKeys}
            variant="outline"
            className="w-full border-dashed border-white/20 bg-transparent py-6 text-white hover:border-[#00F0FF]/50"
          >
            {uploadingKeys ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Upload className="mr-2 h-4 w-4" />
            )}
            Upload license keys (CSV or TXT, one per line)
          </Button>
          {stats && stats.unused < 10 && stats.total > 0 && (
            <div className="flex items-center gap-2 rounded-md bg-amber-500/10 p-2 text-xs text-amber-200">
              <AlertTriangle className="h-3.5 w-3.5" />
              Low inventory — only {stats.unused} key{stats.unused === 1 ? '' : 's'} left
            </div>
          )}
        </div>
      )}

      {method === 'webhook' && (
        <div className="rounded-lg border border-white/10 bg-neutral-900 p-4 space-y-3">
          <div>
            <Label className="mb-1 block text-xs uppercase tracking-wider text-white/50">
              Your provisioning endpoint URL
            </Label>
            <Input
              data-testid="webhook-url-input"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              placeholder="https://api.yourapp.com/theailedger-purchase"
              className="bg-neutral-950 border-white/10 text-white"
            />
            <p className="mt-1 text-xs text-white/40">
              We'll POST a signed payload here on each purchase. Respond with
              JSON: <code className="rounded bg-white/10 px-1">{`{"license_key":"...", "instructions":"..."}`}</code>
              {' '}or <code className="rounded bg-white/10 px-1">{`{"async":true}`}</code> if you'll deliver yourself.
              {' '}<a
                href="/docs/webhooks"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="webhook-docs-link"
                className="text-[#00F0FF] hover:underline"
              >
                Read the docs →
              </a>
            </p>
          </div>

          {savedSecret && (
            <div>
              <Label className="mb-1 block text-xs uppercase tracking-wider text-white/50">
                HMAC signing secret
              </Label>
              <div className="flex gap-2">
                <code
                  data-testid="webhook-secret-display"
                  className="flex-1 truncate rounded border border-white/10 bg-neutral-950 px-3 py-2 font-mono text-xs text-white"
                >
                  {secretRevealed ? savedSecret : '•'.repeat(40)}
                </code>
                <Button
                  size="icon"
                  variant="outline"
                  onClick={() => setSecretRevealed((v) => !v)}
                  className="border-white/20 bg-transparent text-white"
                >
                  {secretRevealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
                <Button
                  size="icon"
                  variant="outline"
                  onClick={copySecret}
                  className="border-white/20 bg-transparent text-white"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <p className="mt-1 text-xs text-white/40">
                Verify <code>X-Theailedger-Signature</code> header in your handler:
                {' '}<code>v1=hex(hmac_sha256(secret, "{`{timestamp}.{body}`}"))</code>
              </p>
            </div>
          )}

          <Button
            data-testid="webhook-test-button"
            onClick={fireTest}
            disabled={testing || !product?.webhook_secret}
            variant="outline"
            className="w-full border-[#00F0FF]/30 bg-[#00F0FF]/5 text-[#00F0FF] hover:bg-[#00F0FF]/10"
          >
            {testing ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <PlayCircle className="mr-2 h-4 w-4" />
            )}
            Send test purchase webhook
          </Button>

          {testResult && (
            <div
              data-testid="webhook-test-result"
              className={`rounded-md border p-3 text-xs ${
                testResult.ok
                  ? 'border-[#00FF94]/30 bg-[#00FF94]/5 text-[#00FF94]'
                  : 'border-red-500/30 bg-red-500/5 text-red-300'
              }`}
            >
              <div className="flex items-center gap-2">
                {testResult.ok ? (
                  <CheckCircle2 className="h-4 w-4" />
                ) : (
                  <AlertTriangle className="h-4 w-4" />
                )}
                <strong>
                  {testResult.ok ? 'Success' : 'Failed'}{' '}
                  {testResult.status_code && `(HTTP ${testResult.status_code})`}
                </strong>
              </div>
              {testResult.license_key && (
                <div className="mt-2">
                  Returned key:{' '}
                  <code className="rounded bg-black/30 px-2 py-0.5">{testResult.license_key}</code>
                </div>
              )}
              {testResult.error && (
                <div className="mt-1 opacity-80">{testResult.error}</div>
              )}
            </div>
          )}
        </div>
      )}

      {method !== 'file' && (
        <div>
          <Label className="mb-1 block text-xs uppercase tracking-wider text-white/50">
            Optional instructions to include in the buyer's email
          </Label>
          <Textarea
            data-testid="delivery-instructions"
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            placeholder="Use your key in the X-API-Key header. Docs at https://yourapp.com/docs/quickstart …"
            className="bg-neutral-900 border-white/10 text-white placeholder:text-white/30"
            rows={3}
          />
        </div>
      )}

      <div className="flex justify-end">
        <Button
          data-testid="delivery-save-button"
          onClick={save}
          disabled={saving}
          className="bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90"
        >
          {saving ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCcw className="mr-2 h-4 w-4" />
          )}
          Save delivery settings
        </Button>
      </div>
    </div>
  );
}

const MethodOption = ({ testId, icon, title, subtitle, selected, onSelect }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onSelect}
    className={`flex items-start gap-3 rounded-lg border p-3 text-left transition-colors ${
      selected
        ? 'border-[#00F0FF] bg-[#00F0FF]/5'
        : 'border-white/10 bg-neutral-900 hover:border-white/30'
    }`}
  >
    <div className={`mt-0.5 flex h-7 w-7 items-center justify-center rounded-md ${
      selected ? 'bg-[#00F0FF]/15 text-[#00F0FF]' : 'bg-white/5 text-white/60'
    }`}>
      {icon}
    </div>
    <div className="flex-1">
      <div className={`text-sm font-medium ${selected ? 'text-[#00F0FF]' : 'text-white'}`}>
        {title}
      </div>
      <div className="text-xs text-white/50">{subtitle}</div>
    </div>
    {selected && <CheckCircle2 className="h-4 w-4 text-[#00F0FF]" />}
  </button>
);

const Stat = ({ label, value, accent }) => (
  <div className="flex-1 rounded-md bg-neutral-950 p-2 text-center">
    <div className={`text-lg font-semibold ${accent}`}>{value}</div>
    <div className="text-[10px] uppercase tracking-wider text-white/50">{label}</div>
  </div>
);
